import {
    ChronoUnit,
    convert, Instant,
    LocalDateTime,
    ZonedDateTime,
    ZoneId,
} from "@js-joda/core";
import { ChronoUnitLegacy, DweenTimeUnit, toDate, toZonedDateTime } from "./time-utils";

export namespace JodaTimeUtils {
    const validHandledUnitsInFunctions = [
        ChronoUnit.MILLIS,
        ChronoUnit.SECONDS,
        ChronoUnit.MINUTES,
        ChronoUnit.HOURS,
        ChronoUnit.DAYS,
        ChronoUnit.WEEKS,
        ChronoUnit.MONTHS,
        ChronoUnit.YEARS
    ];

    export function parseChronoUnit(unit: DweenTimeUnit): ChronoUnit {
        const unitName = ChronoUnitLegacy[unit];
        if (!unitName) {
            throw new Error('Unsupported CHRONOS Unit Legacy value');
        }

        const key = unitName as unknown as keyof typeof ChronoUnit;

        return ChronoUnit[key];
    }

    export const truncateTo = (
      date: ZonedDateTime | Date | string, unit: ChronoUnit,
      zoneId: ZoneId,
    ): Date => {
        if (!validHandledUnitsInFunctions.includes(unit)) {
            throw new Error('Unhandled time unit.');
        }

        const tempDate = toZonedDateTime(date, zoneId);

        const mapFn = (u: ChronoUnit): ZonedDateTime => {
            switch (u) {
                case ChronoUnit.MILLIS:
                case ChronoUnit.SECONDS:
                case ChronoUnit.MINUTES:
                case ChronoUnit.HOURS:
                case ChronoUnit.DAYS:
                    return tempDate.truncatedTo(u);
                case ChronoUnit.WEEKS:
                    return tempDate.truncatedTo(ChronoUnit.DAYS).minusDays(tempDate.dayOfWeek().value() === 0 ? 6 : tempDate.dayOfWeek().value() - 1);
                case ChronoUnit.MONTHS:
                    return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfMonth(1);
                case ChronoUnit.YEARS:
                    return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfMonth(1).withMonth(1);
                default:
                    throw new Error('Unhandled time unit.');
            }
        }

        return toDate(mapFn(unit));
    }

    export const CeilingTo = (
      date: ZonedDateTime | Date | string, unit: ChronoUnit,
      zoneId: ZoneId,
    ): Date => {
        if (!validHandledUnitsInFunctions.includes(unit)) {
            throw new Error('Unhandled time unit.');
        }

        const tempDate = toZonedDateTime(date, zoneId);
        const truncatedDate = toZonedDateTime(JodaTimeUtils.truncateTo(tempDate, unit, zoneId), zoneId);

        const mapFn = (u: ChronoUnit): ZonedDateTime => {
            switch (u) {
                case ChronoUnit.MILLIS:
                case ChronoUnit.SECONDS:
                case ChronoUnit.MINUTES:
                case ChronoUnit.HOURS:
                case ChronoUnit.DAYS:
                case ChronoUnit.WEEKS:
                case ChronoUnit.MONTHS:
                case ChronoUnit.YEARS:
                    if (truncatedDate.isBefore(tempDate)) {
                        return truncatedDate.plus(1, unit);
                    }
                    return truncatedDate;
                default:
                    throw new Error('Unhandled time unit.');
            }
        };

        return toDate(mapFn(unit))
    };


    export const truncateAndOffsetLocalDatetime = (
      date: LocalDateTime,
      truncateToUnit: DweenTimeUnit,
      offset: number,
      offsetUnit: DweenTimeUnit
    ): LocalDateTime => {
        const truncatedDate = date.truncatedTo(parseChronoUnit(truncateToUnit));
        return truncatedDate.plus(offset, parseChronoUnit(offsetUnit));
    }
}

export const getStartOfYesterday = (zoneId: ZoneId): Date => {
    return convert(Instant.now().atZone(zoneId).truncatedTo(ChronoUnit.DAYS).minusDays(1)).toDate();
}

export const getEndOfYesterday = (zoneId: ZoneId): Date => {
    return convert(Instant.now().atZone(zoneId).truncatedTo(ChronoUnit.DAYS).minusSeconds(1)).toDate();
}

